/**
 * ListItemTemplate Component
 *
 * A modal component for selecting item templates with the following features:
 * - Search and filter templates
 * - Category filtering
 * - Material issues filtering (can be hidden)
 * - Template selection with quantity input
 * - Add selected templates to the current context
 *
 * Props:
 * - open: boolean - Controls modal visibility
 * - dialogClose: function - Function to close the modal
 * - hideMaterialIssues: boolean - When true, hides the MaterialIssuesFilter and warning/info icons
 *   Use this prop when you don't want to show the material issues functionality
 *   (e.g., <ListItemTemplate hideMaterialIssues={true} />)
 * - addHandler: function - Function called when templates are added
 * - ...props: Additional props passed through
 */
import React, { useEffect, useState } from "react";
import "./../modal/modal.scss";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import { getItemTemplates } from "../../actions/template-management.action";
import { listTemplateCategories } from "../../actions/inventory.action";
import Form from "react-bootstrap/Form";
import { toUSD } from "../../util/currency-formatter";
import CustomTable from "../../components/table/table";
import Cookies from "js-cookie";
import _ from "lodash";
import MaterialIssuesFilter from "../material-issues-filter";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip } from "@mui/material";
import { NumberInput } from "../inputs/new-input";

function ListItemTemplate({
  open,
  dialogClose,
  hideMaterialIssues = false,
  ...props
}) {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [selectedItem, setSelectedItem] = useState([]);
  const [category, setCategory] = useState([]);
  const [materialIssuesFilter, setMaterialIssuesFilter] = useState("");
  const categoryList = useSelector((state) => state.category.categories) || [];
  const [state, setState] = useState({
    limit: 25,
    totalCount: 0,
    currentPage: 1,
    data: [],
    allPageData: [],
    error: "",
    columns: [
      {
        title: "",
        field: "isSelected",
        class: "w-auto fw-bold",
        render: (rowData) => (
          <div className="table-checkbox table-checkbox-ch text-left">
            <Form.Check
              id={rowData.id}
              type={"checkbox"}
              value={rowData.isSelected}
              checked={Boolean(rowData.isSelected)}
              onChange={(e) => selectHandler(rowData.id, e.target.checked)}
            />
          </div>
        ),
      },
      {
        title: "Name",
        field: "description",
        class: "w-auto fw-bold",
        render: (rowData) => (
          <>
            <div className="d-flex align-items-center">
              <span className="me-2">{rowData.description}</span>
              {!hideMaterialIssues && rowData.hasMovedMaterials && (
                <Tooltip title="Has Moved Materials">
                  <WarningAmberIcon className="text-warning" fontSize="small" />
                </Tooltip>
              )}
              {!hideMaterialIssues && rowData.hasHiddenMaterials && (
                <Tooltip title="Has Hidden Materials">
                  <InfoOutlinedIcon className="text-info" fontSize="small" />
                </Tooltip>
              )}
              {!hideMaterialIssues && rowData.hasDuplicateMaterials && (
                <Tooltip title="Has Duplicate Materials">
                  <WarningAmberIcon className="text-warning" fontSize="small" />
                </Tooltip>
              )}
              {!hideMaterialIssues && rowData.hasInactiveMaterials && (
                <Tooltip title="Has Inactive Materials">
                  <InfoOutlinedIcon className="text-info" fontSize="small" />
                </Tooltip>
              )}
            </div>
          </>
        ),
      },
      {
        title: "Category",
        field: "category_id",
        class: "fw-bold text-right",
        render: (rowData) => (
          <>{(rowData.category && rowData.category.name) || ""}</>
        ),
      },
      {
        title: "Material",
        field: "material_total_cost",
        class: "fw-bold text-right",
        render: (rowData) => <>{toUSD(rowData.material_total_cost)}</>,
      },
      {
        title: "Resource",
        field: "labor_total_cost",
        class: "fw-bold text-right",
        render: (rowData) => <>{toUSD(rowData.labor_total_cost)}</>,
      },
      {
        title: "Contractor",
        field: "subcontractor_total_cost",
        class: "fw-bold text-right",
        render: (rowData) => <>{toUSD(rowData.subcontractor_total_cost)}</>,
      },
      {
        title: "Total",
        field: "total",
        class: "fw-bold text-right",
        render: (rowData) => (
          <>
            {toUSD(
              parseFloat(rowData.material_total_cost || 0) +
                parseFloat(rowData.labor_total_cost || 0) +
                parseFloat(rowData.subcontractor_total_cost || 0)
            )}
          </>
        ),
      },
      {
        title: "Quantity",
        field: "",
        class: "fw-bold",
        render: (rowData) => (
          <div className="input-block input-block-sm mt-0">
            <NumberInput
              name="selectedQty"
              label=""
              placeholder=""
              value={rowData.selectedQty || ""}
              decimalPlaces={2} // or 0 if you want integer quantities
              dataCy="selected-qty-input"
              customInputClass="form-control-input mt-0"
              onChange={(e) => {
                // NumberInput sends an event-like object: { target: { name, value } }
                const val = e.target.value;

                // Keep your existing non‑negative check
                if (val === "" || Number(val) >= 0) {
                  changeHandler(rowData.id, e.target);
                }
              }}
            />
          </div>
        ),
      },
    ],
  });

  const [offset, setOffset] = useState(0);

  const handleLimitChange = (newLimit) => {
    setState((prevState) => ({
      ...prevState,
      limit: newLimit,
    }));
    setOffset(0);
  };

  const fetchItemTemplates = () => {
    const recordsPerPage = Cookies.get("recordsPerPage")
      ? parseInt(Cookies.get("recordsPerPage"))
      : 25;
    const requestParams = { search, offset, limit: recordsPerPage, category };

    // Only include materialIssues filter when not hidden
    if (!hideMaterialIssues) {
      requestParams.materialIssues = materialIssuesFilter;
    }
    dispatch(getItemTemplates(requestParams))
      .then((response) => {
        const newData = response.items || [];
        const updatedData = newData.map((item) => {
          const selected = selectedItem.find((sel) => sel.id === item.id);
          return selected
            ? { ...item, isSelected: true, selectedQty: selected.selectedQty }
            : item;
        });
        const mergedData = _.uniqBy(
          [...state.allPageData, ...updatedData],
          "id"
        );
        setState((prevState) => ({
          ...prevState,
          data: updatedData,
          allPageData: mergedData.length > 0 ? mergedData : updatedData,
          offset: response.offset || prevState.offset,
          currentPage: response.currentPage || prevState.currentPage,
          totalCount: response.totalCount || 0,
        }));
        setSelectedItem((prev) => {
          const updatedSelected = prev.filter((sel) =>
            mergedData.some((item) => item.id === sel.id)
          );
          return updatedSelected;
        });
      })
      .catch((e) => console.error(e));
  };

  useEffect(() => {
    if (categoryList.length === 0)
      dispatch(listTemplateCategories()).catch((e) => console.log(e));
  }, []);

  useEffect(() => {
    fetchItemTemplates();
  }, [
    offset,
    search,
    state.limit,
    category,
    hideMaterialIssues ? null : materialIssuesFilter,
  ]);

  const handleMaterialIssuesFilterChange = (filterValue) => {
    if (!hideMaterialIssues) {
      setMaterialIssuesFilter(filterValue);
      setOffset(0);
      setState((prevState) => ({
        ...prevState,
        currentPage: 1,
      }));
    }
  };

  const selectHandler = (id, checked) => {
    // Update `isSelected` and `selectedQty` in state.data
    setState((prevState) => {
      const updatedData = prevState.data.map((item) =>
        item.id === id
          ? { ...item, isSelected: checked, selectedQty: checked ? 1 : 0 }
          : item
      );
      return { ...prevState, data: updatedData };
    });

    // Update selectedItem with `selectedQty` set to 1 when checked
    setSelectedItem((prev) => {
      const updatedItems = checked
        ? [...prev, { id, selectedQty: 1 }]
        : prev.filter((item) => item.id !== id);
      return updatedItems;
    });
  };

  const changeHandler = (id, { name, value }) => {
    setState((prevState) => {
      const updatedData = prevState.data.map((item) => {
        if (item.id === id) {
          return { ...item, [name]: value, isSelected: true };
        }
        return item;
      });

      return { ...prevState, data: updatedData };
    });

    setSelectedItem((prev) => {
      const index = prev.findIndex((item) => item.id === id);
      if (index !== -1) {
        const updatedItems = [...prev];
        updatedItems[index] = { ...updatedItems[index], [name]: value };
        return updatedItems;
      } else {
        const newSelectedItem = [...prev, { id, [name]: value }];
        return newSelectedItem;
      }
    });
  };

  const closeDialog = () => {
    if (dialogClose) dialogClose();
  };

  const addHandler = () => {
    const dataToUse = state.allPageData;
    const selected = selectedItem
      .map(({ id, selectedQty }) => {
        const foundItem = dataToUse.find((item) => item.id === id);
        if (!foundItem) {
          return null;
        }
        return { ...foundItem, selectedQty };
      })
      .filter((item) => item !== null);
    if (selected.length < 1) {
      setState((prev) => ({
        ...prev,
        error: "Please select at least one item with a quantity of minimum 1",
      }));
    } else {
      setState((prev) => ({
        ...prev,
        error: "",
      }));
      props.addHandler(selected);
    }
  };

  const updateSearch = (value) => {
    if (value == null) return;
    if (value === search) return;
    setSearch(value);
    if (!hideMaterialIssues) {
      setMaterialIssuesFilter("");
    }
    setOffset(0);
  };

  const updateOffset = (pageNumber, limit) => {
    const validPageNumber = pageNumber > 0 ? pageNumber : 1;
    if (!limit) return;
    const _offset = (validPageNumber - 1) * limit;
    setOffset(_offset);

    setState((prevState) => ({
      ...prevState,
      currentPage: validPageNumber,
    }));
  };
  const onCategoryChangeHandler = (selectedCategoryValues) => {
    setCategory(selectedCategoryValues);
    if (!hideMaterialIssues) {
      setMaterialIssuesFilter("");
    }
    setOffset(0);
    setState((prevState) => ({
      ...prevState,
      currentPage: 1,
    }));
  };
  return (
    <Modal
      show={open}
      onHide={closeDialog}
      dialogClassName="modal-70w create-modal"
      aria-labelledby="example-custom-modal-styling-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="example-custom-modal-styling-title">
          Select Item Template
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="purchase-order-block mt-3">
          {!hideMaterialIssues && (
            <div className="d-flex justify-content-end mb-3">
              <MaterialIssuesFilter
                selectedFilter={materialIssuesFilter}
                onFilterChange={handleMaterialIssuesFilterChange}
              />
            </div>
          )}
          <CustomTable
            key={`${
              !hideMaterialIssues ? materialIssuesFilter : ""
            }-${category.join(",")}`}
            columns={state.columns}
            data={state.data}
            paginationCount={state.totalCount}
            pageHandler={updateOffset}
            itemsPerPage={state.limit}
            limitChangeHandler={handleLimitChange}
            fileName="Item Template List"
            searchHandler={updateSearch}
            currentPage={state.currentPage}
            categoryFilterProps={{
              categories: categoryList,
              selectedCategories: category,
              onCategoryChange: onCategoryChangeHandler,
              label: "Category Filter",
            }}
          />
          {state.error && (
            <div className="col-12 d-flex text-danger">{state.error}</div>
          )}
          <div className="col-12 d-flex mt-3">
            <Button onClick={addHandler} className="ms-auto me-3 secondarybtn">
              Add
            </Button>
            <Button
              onClick={closeDialog}
              className="secondarybtn cancelbtn me-auto"
            >
              CANCEL
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ListItemTemplate;
